import type { GeoJSONSource, MapRef } from 'react-map-gl';
import { Map as MapBox, NavigationControl, useMap } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import type mapboxgl from 'mapbox-gl';

import { theme } from '../../styles/theme';
import { ExpandoBox } from '../ExpandoBox';
import { CountryCard } from '../Card/CountryCard';
import { CityCard } from '../Card/CityCard';
import { ProgrammeCard } from '../Card/ProgrammeCard';
import { CaseStudyCard } from '../Card/CaseStudyCard';
import { useMapData } from '../../map-data/use-map-data';
import { usePoiFilterOptions } from '../../map-data/use-filter-options';
import { useFilterMapData } from '../../map-data/use-filter-poi-map-data';
import { usePoiSelectedLocations } from '../PointOfInterestExperience/PoiSelectedLocationsProvider';
import {
  usePoiNavigationArray,
  useCaseStudiesNavigationArray,
  type ProgrammeHierarchyItem,
  type CaseStudyNavigationItem,
} from '../../map-data/use-poi-hierarchy';
import { useCaseStudiesLayer } from '../../map-data/CaseStudiesLayerProvider';
import type { CaseStudyWithLocation } from '../../map-data/use-case-studies-data';
import { useCaseStudiesData } from '../../map-data/use-case-studies-data';

import { PinMapLayers } from './PinMapLayers';
import { PinMapDataLayers } from './PinMapDataLayers';
import { CaseStudiesLayer } from './CaseStudiesLayer';
import { LayerToggle } from './LayerToggle';
import { PoiMapFilters } from './PoiMapFilters';
import { PoiMapCountrySource } from './PoiMapCountrySource';
import { clusterLayerId, unclusteredPointLayer } from './layers';

const useStyles = createUseStyles({
  container: {
    position: 'relative',
  },
});

const mapBoxToken = process.env.REACT_APP_MAP_BOX_TOKEN;

interface Props {
  visible?: boolean;
  drawerOpen?: boolean;
}

const ZOOM_PROGRAMME_THRESHOLD = 11;
const SCREEN_SPEED = 8;

export function PinMap(props: Props) {
  const classes = useStyles();

  const [expand, setExpand] = useState<boolean | null>(null);
  const [selectedCaseStudy, setSelectedCaseStudy] =
    useState<CaseStudyWithLocation | null>(null);
  const [showCaseStudyCard, setShowCaseStudyCard] = useState<boolean>(false);
  const [hoveredCountryIso, setHoveredCountryIso] = useState<string | null>(
    null,
  );

  // Track map container width for responsive offset calculation
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [mapContainerWidth, setMapContainerWidth] = useState(0);

  const { loading, poiCountryData, poiStudyData, poiCityData, poiRegionData } =
    useMapData();
  const { filteredPoiCityMetaData, filteredStudiesById, filteredPoiStudyData } =
    useFilterMapData();
  const { poiMap } = useMap();

  const {
    selectedProgramId,
    setSelectedProgramId,
    selectedCountryName,
    setSelectedCountryName,
    selectedCountrySubDivisionName,
    setSelectedCountrySubDivisionName,
    showCountryCard,
    setShowCountryCard,
    showCityCard,
    setShowCityCard,
    showProgramCard,
    setShowProgramCard,
  } = usePoiSelectedLocations();

  const { filterOptions } = usePoiFilterOptions();
  const { showCaseStudiesLayer, setCaseStudiesLayerVisible } =
    useCaseStudiesLayer();
  const caseStudiesData = useCaseStudiesData();

  const selectedCountry = useMemo(
    () => poiCountryData?.countries?.find(c => c.name === selectedCountryName),
    [poiCountryData, selectedCountryName],
  );

  const selectedCountryIso = useMemo(
    () => selectedCountry?.iso3166_1_2_Code ?? null,
    [selectedCountry],
  );

  const selectedCity = useMemo(
    () =>
      poiCityData?.cities?.find(
        c => c.name === selectedCountrySubDivisionName?.city,
      ),
    [poiCityData, selectedCountrySubDivisionName],
  );

  const selectedRegion = useMemo(
    () =>
      poiRegionData?.regions?.find(
        c => c.name === selectedCountrySubDivisionName?.region,
      ),
    [poiRegionData?.regions, selectedCountrySubDivisionName],
  );

  const selectedStudy = useMemo(
    () => poiStudyData?.studies.find(e => e.id === selectedProgramId),
    [poiStudyData, selectedProgramId],
  );

  // Use shared hook for navigation array - DRY with country list logic
  const navigationArray = usePoiNavigationArray();
  const caseStudiesNavigationArray = useCaseStudiesNavigationArray();

  // Get current position and navigation functions for implementation studies
  const currentIndex = useMemo(() => {
    if (!selectedProgramId) return -1;
    return navigationArray.findIndex(
      item => item.programmeId === selectedProgramId,
    );
  }, [navigationArray, selectedProgramId]);

  // Get current position and navigation functions for case studies
  const currentCaseStudyIndex = useMemo(() => {
    if (!selectedCaseStudy) return -1;
    return caseStudiesNavigationArray.findIndex(
      item => item.caseStudyId === selectedCaseStudy.id,
    );
  }, [caseStudiesNavigationArray, selectedCaseStudy]);

  const { navigatePrevious, navigateNext } = useMemo(() => {
    const canNavigatePrevious = currentIndex > 0;
    const canNavigateNext =
      currentIndex >= 0 && currentIndex < navigationArray.length - 1;

    const navigateToProgramme = (item: ProgrammeHierarchyItem) => {
      setSelectedProgramId(item.programmeId);
      setSelectedCountryName(item.country);
      setSelectedCountrySubDivisionName({
        city: item.city,
        region: item.region,
      });
      setShowCityCard(false);
      setShowCountryCard(false);
      setShowProgramCard(true);
    };

    return {
      navigatePrevious: canNavigatePrevious
        ? () => {
            const prevItem = navigationArray[currentIndex - 1];
            if (prevItem) {
              navigateToProgramme(prevItem);
            }
          }
        : undefined,
      navigateNext: canNavigateNext
        ? () => {
            const nextItem = navigationArray[currentIndex + 1];
            if (nextItem) {
              navigateToProgramme(nextItem);
            }
          }
        : undefined,
    };
  }, [
    currentIndex,
    navigationArray,
    setSelectedProgramId,
    setSelectedCountryName,
    setSelectedCountrySubDivisionName,
    setShowCityCard,
    setShowCountryCard,
    setShowProgramCard,
  ]);

  const { navigatePreviousCaseStudy, navigateNextCaseStudy } = useMemo(() => {
    const canNavigatePrevious = currentCaseStudyIndex > 0;
    const canNavigateNext =
      currentCaseStudyIndex >= 0 &&
      currentCaseStudyIndex < caseStudiesNavigationArray.length - 1;

    const navigateToCaseStudy = (navigationItem: CaseStudyNavigationItem) => {
      // Find the full case study data
      const fullCaseStudy = caseStudiesData?.caseStudies.find(
        cs => cs.id === navigationItem.caseStudyId,
      );
      if (fullCaseStudy) {
        setSelectedCaseStudy(fullCaseStudy);
        setSelectedCountryName(fullCaseStudy.country);
        setSelectedCountrySubDivisionName({
          city: fullCaseStudy.city,
          region: fullCaseStudy.region || '',
        });
        setSelectedProgramId(fullCaseStudy.programme_id);
      }
    };

    return {
      navigatePreviousCaseStudy: canNavigatePrevious
        ? () => {
            const prevItem =
              caseStudiesNavigationArray[currentCaseStudyIndex - 1];
            if (prevItem) {
              navigateToCaseStudy(prevItem);
            }
          }
        : undefined,
      navigateNextCaseStudy: canNavigateNext
        ? () => {
            const nextItem =
              caseStudiesNavigationArray[currentCaseStudyIndex + 1];
            if (nextItem) {
              navigateToCaseStudy(nextItem);
            }
          }
        : undefined,
    };
  }, [
    currentCaseStudyIndex,
    caseStudiesNavigationArray,
    caseStudiesData?.caseStudies,
    setSelectedCaseStudy,
    setSelectedCountryName,
    setSelectedCountrySubDivisionName,
    setSelectedProgramId,
  ]);

  // Calculate card offset based on map container width
  const calculateCardOffset = useMemo(() => {
    // If no card is showing, no offset needed
    if (!showProgramCard && !showCaseStudyCard) {
      return 0;
    }

    // Card dimensions
    const normalCardWidth = 300; // Fixed width from ExpandoBox
    const cardLeftPosition = 60; // Left inset from ExpandoBox
    const expandedCardWidth = mapContainerWidth * 0.85; // 85% when expanded

    // Current card width based on expand state
    const currentCardWidth = expand ? expandedCardWidth : normalCardWidth;

    // Check against MAP CONTAINER WIDTH, not viewport
    // Threshold: 2x (card width + left position) to ensure card takes up max 50% of map width
    const threshold = (cardLeftPosition + currentCardWidth) * 2;

    if (mapContainerWidth > 0 && mapContainerWidth < threshold) {
      return currentCardWidth + cardLeftPosition; // Full card width + left position
    }

    return 0; // No offset on wider map containers
  }, [mapContainerWidth, expand, showProgramCard, showCaseStudyCard]);

  const mapRef = useRef<MapRef>(null);
  const handleMapClick = (event: mapboxgl.MapLayerMouseEvent) => {
    if (!event.features || event.features.length === 0) {
      // Clicked on empty map space - close all cards (clickaway behavior)
      setShowProgramCard(false);
      setShowCaseStudyCard(false);
      setShowCountryCard(false);
      setShowCityCard(false);
      setExpand(null);
      return null;
    }
    const feature = event.features[0];
    if (!feature) return null;
    if (!feature.properties) return null;

    if (feature.properties.cluster) {
      const clusterId = feature.properties.cluster_id;

      // Close all cards when clicking a cluster (zooming to expand it)
      setShowProgramCard(false);
      setShowCaseStudyCard(false);
      setShowCountryCard(false);
      setShowCityCard(false);
      setExpand(null);

      const sourceId = showCaseStudiesLayer
        ? 'case-studies-data'
        : 'data-studies';
      const mapboxSource = mapRef.current?.getSource(sourceId) as GeoJSONSource;

      mapboxSource.getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) {
          return;
        }

        if (feature.geometry.type === 'Point') {
          mapRef.current?.easeTo({
            center: [
              feature.geometry.coordinates[0],
              feature.geometry.coordinates[1],
            ],
            zoom,
            duration: 500,
          });
        }

        /*
         */
      });
    } else {
      const { properties, layer } = feature;

      // Handle country click
      if (layer?.id === 'poi-country-fill') {
        const countryIso2 = properties.iso_3166_1;
        const countryName = poiCountryData?.countries?.find(
          c => c.iso3166_1_2_Code === countryIso2,
        )?.name;

        if (countryName) {
          setSelectedCountryName(countryName);
          setShowCountryCard(true);
          setShowProgramCard(false);
          setShowCaseStudyCard(false);
          setShowCityCard(false);
          setSelectedProgramId(undefined);
          setSelectedCountrySubDivisionName(undefined);
          setExpand(null);
        }
        return;
      }

      if (properties.type === 'case-study') {
        const { geometry } = feature;
        const [longitude, latitude] = (geometry as GeoJSON.Point).coordinates;
        const caseStudy: CaseStudyWithLocation = {
          id: properties.id,
          title: properties.title,
          url: properties.url,
          description: properties.description,
          date: properties.date,
          programme_id: properties.programme_id,
          is_external: properties.is_external,
          latitude,
          longitude,
          studyName: properties.studyName,
          studyAcronym: properties.studyAcronym,
          country: properties.country,
          city: properties.city,
          region: properties.region,
          linkedProgrammeId: properties.linkedProgrammeId,
        };

        setSelectedCaseStudy(caseStudy);
        setShowCaseStudyCard(true);
        setShowCountryCard(false);
        setShowCityCard(false);
        setShowProgramCard(false);
        setExpand(null);

        // Also open the related implementation study in the list view (without showing its card)
        setSelectedCountryName(caseStudy.country);
        setSelectedCountrySubDivisionName({
          city: caseStudy.city,
          region: caseStudy.region,
        });
        setSelectedProgramId(caseStudy.linkedProgrammeId);

        return;
      }

      const program = properties;
      setSelectedProgramId(program.id);
      setSelectedCountryName(program.country);
      setSelectedCountrySubDivisionName({
        city: program.city,
        region: program.region,
      });
      setShowCityCard(false);
      setShowCountryCard(false);
      setShowProgramCard(true);
      setExpand(null);

      if (showCaseStudiesLayer) {
        setCaseStudiesLayerVisible(false);
        setShowCaseStudyCard(false);
      }
    }
  };

  // reset map is resized when it becomes visible
  useEffect(() => {
    if (props.visible) {
      poiMap?.resize();
    }
  }, [props.visible, poiMap]);

  // Resize and re-center map when drawer state changes
  useEffect(() => {
    if (props.drawerOpen !== undefined && poiMap) {
      // Delay to allow CSS transition to complete
      const timeoutId = setTimeout(() => {
        poiMap.resize();

        // Re-center on current selection if any
        if (selectedStudy && showProgramCard) {
          poiMap.flyTo({
            center: [selectedStudy.longitude, selectedStudy.latitude],
            zoom: poiMap.getZoom(),
            duration: 300,
            padding: {
              top: 0,
              left: calculateCardOffset,
              right: 0,
              bottom: 0,
            },
          });
        }
      }, 450); // Match CSS transition duration

      return () => clearTimeout(timeoutId);
    }
  }, [
    props.drawerOpen,
    poiMap,
    selectedStudy,
    showProgramCard,
    calculateCardOffset,
  ]);

  // fly to the study being viewed
  useEffect(() => {
    if (selectedStudy && showProgramCard) {
      poiMap?.flyTo({
        center: [selectedStudy.longitude, selectedStudy.latitude],
        essential: true,
        zoom: ZOOM_PROGRAMME_THRESHOLD,
        screenSpeed: SCREEN_SPEED,
        padding: {
          top: 0,
          left: calculateCardOffset,
          right: 0,
          bottom: 0,
        },
      });
    }
  }, [selectedStudy, showProgramCard, poiMap, calculateCardOffset]);

  // fly to the case study being viewed
  useEffect(() => {
    if (selectedCaseStudy && showCaseStudyCard) {
      poiMap?.flyTo({
        center: [selectedCaseStudy.longitude, selectedCaseStudy.latitude],
        essential: true,
        zoom: ZOOM_PROGRAMME_THRESHOLD,
        screenSpeed: SCREEN_SPEED,
        padding: {
          top: 0,
          left: calculateCardOffset,
          right: 0,
          bottom: 0,
        },
      });
    }
  }, [selectedCaseStudy, showCaseStudyCard, poiMap, calculateCardOffset]);

  // fly to the city being viewed
  useEffect(() => {
    // Only run if showing city card, not program card, and city is selected
    if (!showCityCard || showProgramCard || !selectedCity) {
      return;
    }

    let boundaryBox;

    if (
      selectedCity?.name.toLowerCase() === 'national' &&
      selectedCountry?.boundaryBox
    ) {
      // just focus on the country
      boundaryBox = selectedCountry?.boundaryBox;
    } else if (selectedCity?.boundaryBox) {
      // it would be nice to use the bounds of the markers, have than the city boundary
      boundaryBox = selectedCity?.boundaryBox;
    }

    if (boundaryBox && poiMap) {
      const currentZoom = poiMap.getZoom();
      const targetZoom = Math.max(currentZoom - 1, 3); // Zoom out by 1, but not below 3

      // Calculate center of boundary box
      const centerLng = ((boundaryBox[0] ?? 0) + (boundaryBox[2] ?? 0)) / 2;
      const centerLat = ((boundaryBox[1] ?? 0) + (boundaryBox[3] ?? 0)) / 2;

      poiMap.flyTo({
        center: [centerLng, centerLat],
        zoom: targetZoom,
        duration: 1000,
        essential: true,
      });
    }
  }, [selectedCity, selectedCountry, poiMap, showCityCard, showProgramCard]);

  // fly to the region being viewed
  useEffect(() => {
    // Only run if region is selected and not showing program card
    if (!selectedRegion || showProgramCard) {
      return;
    }

    let boundaryBox;

    if (
      selectedRegion?.name.toLowerCase() === 'national' &&
      selectedCountry?.boundaryBox
    ) {
      // just focus on the country
      boundaryBox = selectedCountry?.boundaryBox;
    } else if (selectedRegion?.boundaryBox) {
      // it would be nice to use the bounds of the markers, have than the city boundary
      boundaryBox = selectedRegion?.boundaryBox;
    }

    if (boundaryBox && poiMap) {
      poiMap.fitBounds(
        [
          [boundaryBox[0] ?? 0, boundaryBox[1] ?? 0],
          [boundaryBox[2] ?? 0, boundaryBox[3] ?? 0],
        ],
        { padding: 40, duration: 1000 },
      );
    }
  }, [selectedRegion, selectedCountry, poiMap, showProgramCard]);

  // fly to the country being viewed
  useEffect(() => {
    const { boundaryBox } = selectedCountry ?? {};

    if (boundaryBox && showCountryCard) {
      poiMap?.fitBounds(
        [
          [boundaryBox[0] ?? 0, boundaryBox[1] ?? 0],
          [boundaryBox[2] ?? 0, boundaryBox[3] ?? 0],
        ],
        { padding: 50, duration: 1000 },
      );
    }
  }, [selectedCountry, poiMap, showCountryCard]);

  // Track map container width with ResizeObserver
  useEffect(() => {
    const container = mapContainerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        setMapContainerWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(container);

    // Set initial width
    setMapContainerWidth(container.clientWidth);

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (showCaseStudiesLayer) {
      setShowProgramCard(false);
      setShowCityCard(false);
      setShowCountryCard(false);
    } else {
      setShowCaseStudyCard(false);
    }
    setExpand(null);
  }, [
    showCaseStudiesLayer,
    setShowProgramCard,
    setShowCityCard,
    setShowCountryCard,
    setShowCaseStudyCard,
  ]);

  useEffect(() => {
    if (showCountryCard || showCityCard) {
      setShowCaseStudyCard(false);
      setExpand(null);
    }
  }, [showCountryCard, showCityCard]);

  // Handle card expansion/collapse - adjust offset dynamically
  useEffect(() => {
    // Only adjust if expand state is true or false (not null = not initial open)
    if ((showProgramCard || showCaseStudyCard) && expand !== null) {
      poiMap?.easeTo({
        padding: {
          top: 0,
          left: calculateCardOffset,
          right: 0,
          bottom: 0,
        },
        duration: 300, // Match card expand animation
      });
    }
  }, [expand, calculateCardOffset, showProgramCard, showCaseStudyCard, poiMap]);

  // Handle map container resize - re-center on selected pin with new offset
  useEffect(() => {
    // Only run if width changed AFTER initial mount and a card is already showing
    if (mapContainerWidth > 0 && (showProgramCard || showCaseStudyCard)) {
      let centerCoords: [number, number] | undefined;

      if (showProgramCard && selectedStudy) {
        centerCoords = [selectedStudy.longitude, selectedStudy.latitude];
      } else if (showCaseStudyCard && selectedCaseStudy) {
        centerCoords = [
          selectedCaseStudy.longitude,
          selectedCaseStudy.latitude,
        ];
      }

      if (centerCoords) {
        // Add a delay to let the map settle after resize
        const timeoutId = setTimeout(() => {
          poiMap?.easeTo({
            center: centerCoords,
            padding: {
              top: 0,
              left: calculateCardOffset,
              right: 0,
              bottom: 0,
            },
            duration: 500,
          });
        }, 250); // 250ms delay to let map settle

        return () => clearTimeout(timeoutId);
      }
    }
  }, [
    mapContainerWidth,
    calculateCardOffset,
    poiMap,
    selectedCaseStudy,
    selectedStudy,
    showCaseStudyCard,
    showProgramCard,
  ]);

  // Reset offset when cards close
  useEffect(() => {
    // When no cards are showing, remove offset
    // Only if there's a selected study (otherwise it's a full reset, handled elsewhere)
    // Don't reset if showing country/city card (they handle their own bounds)
    if (
      !showProgramCard &&
      !showCaseStudyCard &&
      !showCountryCard &&
      !showCityCard &&
      selectedStudy
    ) {
      poiMap?.easeTo({
        padding: { top: 0, left: 0, right: 0, bottom: 0 },
        duration: 500,
      });
    }
  }, [
    showProgramCard,
    showCaseStudyCard,
    showCountryCard,
    showCityCard,
    selectedStudy,
    poiMap,
  ]);

  const [shouldAnimate, setShouldAnimate] = useState<boolean>(false);

  useEffect(() => {
    const pulseMarker = () => {
      if (!shouldAnimate || !mapRef.current) return;

      const map = mapRef.current.getMap();

      if (showCaseStudiesLayer) {
        const radius = map.getPaintProperty(
          'case-studies-point',
          'circle-radius',
        );

        map.setPaintProperty(
          'case-studies-point',
          'circle-radius',
          radius > 10 ? 8 : 11,
        );

        const stroke = map.getPaintProperty(
          'case-studies-clusters',
          'circle-stroke-width',
        );

        map.setPaintProperty(
          'case-studies-clusters',
          'circle-stroke-width',
          stroke > 8 ? 8 : 12,
        );
      } else {
        const radius = map.getPaintProperty(
          unclusteredPointLayer.id,
          'circle-radius',
        );

        map.setPaintProperty(
          unclusteredPointLayer.id,
          'circle-radius',
          radius > 10 ? 8 : 11,
        );

        // Note: Cluster layer no longer exists - clusters now use custom HTML markers
        // so no pulsing animation needed for clusters
      }
    };

    const intervalTimer = setInterval(pulseMarker, 1000);

    return () => clearInterval(intervalTimer);
  }, [shouldAnimate, showCaseStudiesLayer]);

  const [cursor, setCursor] = useState<string>('auto');
  const onMouseEnter = useCallback(() => setCursor('pointer'), []);
  const onMouseLeave = useCallback(() => setCursor('auto'), []);

  const onMouseMove = useCallback((e: mapboxgl.MapLayerMouseEvent) => {
    const feature = e.features && e.features[0];
    if (feature?.layer?.id === 'poi-country-fill') {
      const iso2 = feature?.properties?.iso_3166_1 ?? null;
      setHoveredCountryIso(iso2);
      if (e.target && 'getCanvas' in e.target) {
        e.target.getCanvas().style.cursor = iso2 ? 'pointer' : '';
      }
    } else {
      setHoveredCountryIso(null);
    }
  }, []);

  const interactiveLayerIds = useMemo(() => {
    const layers = showCaseStudiesLayer
      ? ['case-studies-clusters', 'case-studies-point']
      : [clusterLayerId, unclusteredPointLayer.id].filter(item => !!item);

    // Add country fill layer as interactive
    return [...layers, 'poi-country-fill'];
  }, [showCaseStudiesLayer]);

  const handleStudyClick = useCallback(() => {
    if (selectedCaseStudy) {
      setSelectedCountryName(selectedCaseStudy.country);
      setSelectedCountrySubDivisionName({
        city: selectedCaseStudy.city,
        region: selectedCaseStudy.region,
      });
      setSelectedProgramId(selectedCaseStudy.programme_id);

      setCaseStudiesLayerVisible(false);
      setShowCaseStudyCard(false);
      setShowProgramCard(true);
      setShowCountryCard(false);
      setShowCityCard(false);
      setExpand(null);
    }
  }, [
    selectedCaseStudy,
    setSelectedProgramId,
    setSelectedCountryName,
    setSelectedCountrySubDivisionName,
    setCaseStudiesLayerVisible,
    setShowCaseStudyCard,
    setShowProgramCard,
    setShowCountryCard,
    setShowCityCard,
  ]);

  if (
    loading ||
    !poiCountryData ||
    !filteredPoiCityMetaData ||
    !poiStudyData ||
    !filteredStudiesById ||
    !poiCityData
  ) {
    // handle loading here
    return null;
  }

  return (
    <div
      data-tour="poi-map-wrapper"
      className={classes.container}
      ref={mapContainerRef}
    >
      <MapBox
        initialViewState={{
          longitude: 0,
          latitude: 20,
          zoom: 0.7,
        }}
        id="poiMap"
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/lcpn-interactive-map/cl70hv5t8000v14p7f89wptcu"
        mapboxAccessToken={mapBoxToken}
        dragRotate={false}
        maxZoom={16}
        minZoom={0.7}
        // onClick={resetNavigationBar}
        interactiveLayerIds={interactiveLayerIds}
        onClick={handleMapClick}
        ref={mapRef}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onMouseMove={onMouseMove}
        onSourceData={() => {
          setShouldAnimate(true);
        }}
        cursor={cursor}
      >
        <PoiMapFilters />

        <PoiMapCountrySource
          hoveredCountryIso={hoveredCountryIso}
          selectedCountryIso={selectedCountryIso}
        />

        <PinMapLayers />
        <NavigationControl showCompass={false} position={'bottom-left'} />
        <LayerToggle />

        <PinMapDataLayers
          filteredPoiStudyData={filteredPoiStudyData}
          filteredStudiesById={filteredStudiesById}
          filterOptions={filterOptions}
        />

        <CaseStudiesLayer />

        {showCaseStudyCard && selectedCaseStudy && (
          <ExpandoBox
            inset={`max(15%, ${theme.spacing(14)}) 0 ${theme.spacing(
              2.5,
            )} ${theme.spacing(15)}`}
            expand={expand}
            width={300}
            minHeight={'30em'}
          >
            <CaseStudyCard
              title={selectedCaseStudy.title}
              description={selectedCaseStudy.description}
              url={selectedCaseStudy.url}
              date={selectedCaseStudy.date}
              country={selectedCaseStudy.country}
              city={selectedCaseStudy.city}
              studyName={selectedCaseStudy.studyName}
              studyAcronym={selectedCaseStudy.studyAcronym}
              isExternal={selectedCaseStudy.is_external}
              closeCard={value => {
                setShowCaseStudyCard(value);
                setExpand(false);
              }}
              handleExpand={value => setExpand(value)}
              expand={expand}
              onStudyClick={handleStudyClick}
              onNavigatePrevious={navigatePreviousCaseStudy}
              onNavigateNext={navigateNextCaseStudy}
            />
          </ExpandoBox>
        )}

        {showProgramCard &&
          selectedProgramId &&
          filteredStudiesById[selectedProgramId] && (
            <ExpandoBox
              inset={`max(15%, ${theme.spacing(14)}) 0 ${theme.spacing(
                2.5,
              )} ${theme.spacing(15)}`}
              expand={expand}
              width={300}
              minHeight={'30em'}
            >
              <ProgrammeCard
                cityName={filteredStudiesById[selectedProgramId].city}
                countryName={filteredStudiesById[selectedProgramId].country}
                implementation={filteredStudiesById[selectedProgramId].design}
                studyAcronym={
                  filteredStudiesById[selectedProgramId].studyAcronym
                }
                studyName={filteredStudiesById[selectedProgramId].studyName}
                studyId={selectedProgramId}
                information={
                  filteredStudiesById[selectedProgramId].description ?? ''
                }
                closeCard={value => {
                  setShowProgramCard(value);
                  setExpand(false);
                }}
                handleExpand={value => setExpand(value)}
                region={filteredStudiesById[selectedProgramId].region}
                expand={expand}
                onNavigatePrevious={navigatePrevious}
                onNavigateNext={navigateNext}
                onRegionClick={regionName => {
                  setSelectedCountrySubDivisionName({
                    region: regionName,
                  });
                  setSelectedProgramId('');
                }}
                onCityClick={cityName => {
                  setSelectedCountrySubDivisionName({
                    city: cityName,
                  });
                  setSelectedProgramId('');
                }}
                onCountryClick={countryName => {
                  setSelectedCountrySubDivisionName({});
                  setSelectedProgramId('');
                }}
              />
            </ExpandoBox>
          )}

        {showCityCard &&
          selectedCity &&
          filteredPoiCityMetaData[selectedCity.name].length > 0 && (
            <div
              style={{
                position: 'absolute',
                top: `max(15%, ${theme.spacing(14)})`,
                left: theme.spacing(15),
              }}
            >
              <CityCard
                cityName={selectedCity?.name}
                countryName={selectedCity?.countryLocated}
                totalNationalProgrammes={selectedCity.nationalProgrammes ?? 0}
                totalRegionalProgrammes={selectedCity.regionalProgrammes ?? 0}
                totalStudiesProgrammes={selectedCity.studyProgrammes ?? 0}
                totalClinicalProgrammes={selectedCity.clinicalProgrammes ?? 0}
                closeCard={value => {
                  setShowCityCard(value);
                }}
              />
            </div>
          )}

        {showCountryCard && selectedCountry && (
          <div
            style={{
              position: 'absolute',
              top: 'max(15%, 55px)',
              left: '60px',
            }}
          >
            <CountryCard
              countryName={selectedCountry.name}
              countryCode={selectedCountry.iso3166_1_2_Code}
              totalNationalProgrammes={selectedCountry.nationalProgrammes ?? 0}
              totalRegionalProgrammes={selectedCountry.regionalProgrammes ?? 0}
              totalClinicalProgrammes={selectedCountry.clinicalProgrammes ?? 0}
              totalStudiesProgrammes={selectedCountry.studyProgrammes ?? 0}
              closeCard={value => {
                setShowCountryCard(value);
              }}
              currentExperience="poi"
            />
          </div>
        )}
      </MapBox>
    </div>
  );
}
